import type { Book } from "@book-club/shared";
import { useEffect, useState } from "react";
import type { SortColumn } from "@/features/books/constants/sortColumns";
import type { BookWithStats, FilterType } from "@/features/books/types";
import { formatBookRating, getBookAverageRating, getBookFeaturedDate } from "@/features/books/utils/bookStatsUtils";
import { applyBookFilters, filterBooks, getFilterCounts, sortBooks } from "@/features/books/utils/bookUtils";

const PAGE_SIZE = 25;

// Define default sort direction for each column
const DEFAULT_SORT_DIRECTIONS: Record<SortColumn, "asc" | "desc"> = {
    author: "asc",
    createdAt: "desc",
    featuredDate: "desc",
    rating: "desc",
    title: "asc",
};

type UseBookLibraryReturn = {
    // State
    searchQuery: string;
    sortColumn: SortColumn;
    sortDirection: "asc" | "desc";
    currentPage: number;
    activeFilters: Set<FilterType>;
    filterCounts: Record<FilterType, number>;

    // Actions
    setSearchQuery: (query: string) => void;
    handleSort: (column: SortColumn) => void;
    setCurrentPage: (page: number) => void;
    toggleFilter: (filterType: FilterType) => void;
    clearFilters: () => void;

    // Computed values
    booksWithStats: BookWithStats[];
    sortedBooks: BookWithStats[];
    paginatedBooks: BookWithStats[];
    totalPages: number;
    startIndex: number;
    endIndex: number;
};

/**
 * Transforms books to BookWithStats by adding computed fields
 */
function transformToBookWithStats(books: Book[]): BookWithStats[] {
    return books.map((book) => ({
        ...book,
        author: book.type === "specificBook" || book.type === "authorFocus" ? book.author : "",
        averageRating: getBookAverageRating(book),
        featuredDate: getBookFeaturedDate(book),
        ratingDisplay: formatBookRating(getBookAverageRating(book)),
        title: book.type === "specificBook" ? book.title : "",
    }));
}

/**
 * Encapsulates search, filter, sort, and pagination logic for the Book Library.
 * Books are pre-filtered by the caller (visible vs archived queries).
 */
export function useBookLibrary(books: Book[]): UseBookLibraryReturn {
    const [searchQuery, setSearchQuery] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn>("title");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set());

    const booksWithStats = transformToBookWithStats(books);
    const filterCounts = getFilterCounts(booksWithStats);

    const searchFiltered = filterBooks(booksWithStats, searchQuery);
    const bookFiltered = applyBookFilters(searchFiltered, activeFilters);
    const sortedBooks = sortBooks(bookFiltered, sortColumn, sortDirection);

    const totalPages = Math.ceil(sortedBooks.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, totalPages]);

    // Reset to page 1 when search query or filters change
    useEffect(() => {
        setCurrentPage(1);
    }, []);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection(DEFAULT_SORT_DIRECTIONS[column]);
        }
        setCurrentPage(1);
    };

    const toggleFilter = (filterType: FilterType) => {
        setActiveFilters((prev) => {
            const next = new Set(prev);
            if (next.has(filterType)) {
                next.delete(filterType);
            } else {
                next.add(filterType);
            }
            return next;
        });
    };

    const clearFilters = () => {
        setActiveFilters(new Set());
    };

    return {
        activeFilters,
        booksWithStats,
        clearFilters,
        currentPage,
        endIndex,
        filterCounts,
        handleSort,
        paginatedBooks,
        searchQuery,
        setCurrentPage,
        setSearchQuery,
        sortColumn,
        sortDirection,
        sortedBooks,
        startIndex,
        toggleFilter,
        totalPages,
    };
}
